import { Capacitor } from '@capacitor/core'
import { Global } from '@emotion/react'
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { BleManagerProvider } from 'App/ServiceCenter/BluetoothLowEnergy/BleManager'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary'
import { HubMaintenance } from 'components/HubMaintenance/HubMaintenance'
import { IntercomProvider } from 'components/Intercom/IntercomProvider'
import { MiniStatusbar } from 'components/MiniStatusbarOverlay/MiniStatusbarOverlay'
import { ModalsProvider } from 'components/ModalsProvider/ModalsProvider'
import { NfcScanner } from 'components/NfcScanner/NfcScanner'
import { QrCodeScanner } from 'components/QrCodeScanner/QrCodeScanner'
import { I18nWrapper } from 'i18n/I18nWrapper'
import { useEffect } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { customTheme } from 'settings/ui-theme'
import { smallStore } from 'stores/smallStore/smallStore'
import { CustomRouter, history } from 'utils/router'
import { followEnvSafeAreaInsets } from 'utils/safeAreaInsets/followEnvSafeAreaInsets'
import { AppRoutes } from './AppRoutes'
import { MapProvider } from './Map/MapContext/MapProvider'
import { LnrBleNodeFactory } from './ServiceCenter/BluetoothLowEnergy/models/LnrNode/lnrBleNode'

export const App = () => {
  useEffect(() => {
    let envInsets: ReturnType<typeof followEnvSafeAreaInsets> | null = null
    let safeAreaListener: ReturnType<typeof SafeArea.addListener> | null = null
    let insetObserver: MutationObserver | null = null

    if (Capacitor.getPlatform() === 'ios') {
      envInsets = followEnvSafeAreaInsets()

      safeAreaListener = SafeArea.addListener('safeAreaChanged', envInsets.refresh)
    }

    if (Capacitor.getPlatform() === 'android') {
      // SystemBars writes the insets the web view needs into --safe-area-inset-* (0 when it pads the web view itself,
      // below WebView 140). The safe-area plugin reports raw window insets, so it's only used on iOS
      const syncAndroidInsets = () => {
        const style = getComputedStyle(document.documentElement)
        const read = (side: string) => parseInt(style.getPropertyValue(`--safe-area-inset-${side}`)) || 0
        const insets = { top: read('top'), right: read('right'), bottom: read('bottom'), left: read('left') }

        smallStore.setState((s) => ({ ...s, safeArea: { insets } }))
      }

      syncAndroidInsets()

      insetObserver = new MutationObserver(syncAndroidInsets)

      insetObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style'],
      })
    }

    if (Capacitor.getPlatform() === 'web') {
      envInsets = followEnvSafeAreaInsets()
    }

    return () => {
      envInsets?.stop()

      safeAreaListener?.then((listener) => listener.remove())

      insetObserver?.disconnect()
    }
  }, [])

  return (
    <I18nWrapper>
      <Global
        styles={{
          '*,*::before,*::after': { boxSizing: 'border-box' },
          'body': {
            fontFamily: 'Roboto, Helvetica Neue, Helvetica, Arial, sans-serif',
            margin: 0,
            padding: 0,
          },
        }}
      />
      <BleManagerProvider factories={[LnrBleNodeFactory]}>
        <CustomRouter history={history}>
          <MantineProvider theme={customTheme}>
            <MapProvider>
              <ErrorBoundary>
                <IntercomProvider>
                  {/* the notch offset lives in mantine-overrides.css, scoped to the top containers */}
                  <Notifications position="top-center" zIndex={SharedSettings.DEADULT_NOTIFICATION_Z_INDEX} />
                  <MiniStatusbar.Overlay />
                  <QrCodeScanner.Overlay />
                  <NfcScanner.Overlay />
                  <AboveAllModal.Overlay />
                  <ModalsProvider>
                    <HubMaintenance>
                      <AppRoutes />
                    </HubMaintenance>
                  </ModalsProvider>
                </IntercomProvider>
              </ErrorBoundary>
            </MapProvider>
          </MantineProvider>
        </CustomRouter>
      </BleManagerProvider>
    </I18nWrapper>
  )
}
